/**
 * Breadcrumb ring-buffer utility for FEED-04.
 *
 * Records the last 10 navigation and click events in sessionStorage so that
 * when a user submits a feedback report, the modal can attach a breadcrumb
 * trail showing how they arrived at the current page.
 *
 * Storage key: 'cogslog.feedback.breadcrumbs'
 * Cap: 10 events (older events are dropped from the front of the array).
 *
 * Event shape:
 * ```
 * { t: string (ISO-8601), type: 'nav'|'click', url: string, label: string|null }
 * ```
 *
 * All sessionStorage operations are wrapped in try/catch — storage exceptions
 * (QuotaExceededError, private-browsing restrictions, etc.) are non-fatal and
 * result in a no-op write or an empty array read.
 */

const STORAGE_KEY = 'cogslog.feedback.breadcrumbs';
const MAX_EVENTS = 10;

/**
 * Push a new breadcrumb event into the ring buffer.
 *
 * If the buffer already contains 10 events, the oldest event is dropped to
 * make room for the new one. The updated array is written back to
 * sessionStorage. Any storage exception is swallowed silently.
 *
 * @param {{ t: string, type: 'nav'|'click', url: string, label: string|null }} event
 *   The breadcrumb event to push.  `t` should be an ISO-8601 timestamp string.
 * @returns {void}
 *
 * @example
 * pushBreadcrumb({ t: new Date().toISOString(), type: 'nav', url: '/dashboard', label: null });
 */
export function pushBreadcrumb(event) {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    let existing = [];
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        existing = Array.isArray(parsed) ? parsed : [];
      } catch {
        existing = [];
      }
    }
    existing.push(event);
    // Trim to the last MAX_EVENTS entries (ring buffer)
    if (existing.length > MAX_EVENTS) {
      existing = existing.slice(existing.length - MAX_EVENTS);
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch {
    // Non-fatal: storage unavailable or quota exceeded
  }
}

/**
 * Read the current breadcrumb buffer from sessionStorage.
 *
 * Returns an empty array if sessionStorage is empty, unavailable, or if the
 * stored value is corrupted JSON.
 *
 * @returns {{ t: string, type: string, url: string, label: string|null }[]}
 *   Array of up to 10 breadcrumb events in chronological order (oldest first).
 *
 * @example
 * const crumbs = getBreadcrumbs();
 * // [{ t: '2026-04-17T…', type: 'nav', url: '/dashboard', label: null }, …]
 */
export function getBreadcrumbs() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  } catch {
    return [];
  }
}

/**
 * Clear the breadcrumb buffer in sessionStorage.
 *
 * Called optionally after a successful feedback submission to reset the trail.
 * Any storage exception is swallowed silently.
 *
 * @returns {void}
 *
 * @example
 * clearBreadcrumbs(); // sessionStorage key removed
 */
export function clearBreadcrumbs() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Non-fatal
  }
}
